import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo, useRef, useEffect, forwardRef, useState, useLayoutEffect } from 'react';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { ArrowRightIcon, ArrowLeftIcon } from '@paypalcorp/pp-react-icons';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { utilStyles } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import { stripUnit, remToPx, math } from 'polished';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSizeMediaMd = tokens.sysSizeMediaMd,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSizeIndicatorLg = tokens.sysSizeIndicatorLg,
      sysColorUnselectedMain = tokens.sysColorUnselectedMain,
      sysColorSelectedMain = tokens.sysColorSelectedMain,
      sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive;
  var peekWidth = Number(stripUnit(remToPx(sysSpaceLg)));
  var mobileItemMargin = Number(stripUnit(remToPx(sysSpaceMd)));
  var itemMargin = Number(stripUnit(remToPx(sysSpaceLg)));

  var _utilStyles = utilStyles(tokens),
      elevationStyles = _utilStyles.elevationStyles,
      variables = _utilStyles.variables;

  var ppuiEaseInOut = variables.ppuiEaseInOut;
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: horizontal_scroll_base;\n    position: relative;\n\n    &:hover {\n      & > div:nth-of-type(2n), // on hover show the navigation buttons\n      & > div:nth-of-type(3n) {\n        opacity: 1;\n      }\n    }\n  "])));
  var itemList = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: item_list;\n    display: flex;\n    flex-direction: row;\n    list-style: none;\n    margin: 0;\n    overflow-y: -moz-hidden-unscrollable;\n    overflow-x: auto;\n    padding: ", " 0;\n\n    & > * {\n      flex: 0 0 auto;\n    }\n  "])), sysSpaceLg);
  var item = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: item;\n    margin: 0 ", "px;\n\n    @media (max-width: ", ") {\n      margin: 0 ", "px;\n    }\n  "])), math("".concat(itemMargin, " / 2")), sysSizeMediaMd, math("".concat(mobileItemMargin, " / 2")));
  var buttonNavigation = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: button_nav;\n    position: absolute;\n    top: 0;\n    display: flex;\n    align-items: center;\n    height: calc(100% - ", " - ", ");\n    opacity: 0;\n    transition: opacity 0.3s ease-in-out;\n    pointer-events: none;\n\n    button {\n      pointer-events: all;\n      ", "\n\n      &:focus:after {\n        display: none;\n      }\n\n      &:hover {\n        ", "\n      }\n    }\n  "])), sysSizeIndicatorLg, sysSpaceLg, elevationStyles.level1, elevationStyles.level3);
  var buttonPrevious = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    ", ";\n    label: buton_previous;\n    left: ", ";\n\n    [dir='rtl'] & {\n      left: unset;\n      right: ", ";\n    }\n  "])), buttonNavigation, sysSpaceLg, sysSpaceLg);
  var buttonNext = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    ", ";\n    label: button_next;\n    right: ", ";\n\n    [dir='rtl'] & {\n      left: ", ";\n      right: unset;\n    }\n  "])), buttonNavigation, sysSpaceLg, sysSpaceLg);
  var itemListDesktop = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: item_list_desktop;\n    overflow-x: visible;\n  "])));
  var pageContainer = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: page_container;\n    overflow-x: hidden;\n  "])));
  var page = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: page;\n    position: relative;\n    left: 0;\n    transition: left 1s ", ";\n\n    [dir='rtl'] & {\n      right: 0;\n      left: unset;\n      transition: right 1s ease-in-out;\n    }\n  "])), ppuiEaseInOut);
  var dots = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: dots;\n    padding-bottom: ", ";\n    text-align: center;\n    line-height: ", ";\n  "])), sysSpaceLg, sysSizeIndicatorLg);
  var dot = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: dot;\n    margin: 0 ", ";\n    height: ", ";\n    width: ", ";\n    border-radius: ", ";\n    background-color: ", ";\n    display: inline-block;\n  "])), sysSpaceSm, sysSizeIndicatorLg, sysSizeIndicatorLg, sysBorderRadiusInteractive, sysColorUnselectedMain);
  var dotSelected = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: dot_selected;\n    background-color: ", ";\n  "])), sysColorSelectedMain);
  /**
   * Get styles for each item in the item list
   * @param props ItemListProps
   * @returns SerializedStyles
   */

  var getFlexBasisItems = function getFlexBasisItems(props) {
    var isMobileBreakpoint = props.isMobileBreakpoint,
        numOfVisibleItems = props.numOfVisibleItems,
        hasFixedSizeItems = props.hasFixedSizeItems;

    if (hasFixedSizeItems) {
      return css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n        label: fixed_size;\n        flex-basis: auto;\n      "])));
    }

    if (isMobileBreakpoint) {
      return css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n        label: mobile;\n        flex-basis: calc(\n          calc(100% - ", "px) / ", " -\n            ", "px\n        );\n      "])), peekWidth + mobileItemMargin / 2, numOfVisibleItems, mobileItemMargin);
    }

    return css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n      label: desktop;\n      flex-basis: calc(100% / ", " - ", "px);\n    "])), numOfVisibleItems, itemMargin);
  };

  var getContainerWidth = function getContainerWidth(pageWidth) {
    var hasFixedSizeItems = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return hasFixedSizeItems && css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n        width: ", "px;\n      "])), pageWidth);
  };

  return {
    base: base,
    itemList: itemList,
    item: item,
    buttonPrevious: buttonPrevious,
    buttonNext: buttonNext,
    itemListDesktop: itemListDesktop,
    pageContainer: pageContainer,
    page: page,
    dots: dots,
    dot: dot,
    dotSelected: dotSelected,
    getFlexBasisItems: getFlexBasisItems,
    getContainerWidth: getContainerWidth,
    peekWidth: peekWidth,
    mobileItemMargin: mobileItemMargin,
    itemMargin: itemMargin
  };
}

var _excluded = ["items", "numOfVisibleItems", "renderItem", "className", "itemClassName", "hasFixedSizeItems", "onItemListScroll", "onInit"];

function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];

  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;

      var F = function F() {};

      return {
        s: F,
        n: function n() {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function e(_e) {
          throw _e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function s() {
      it = it.call(o);
    },
    n: function n() {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function e(_e2) {
      didErr = true;
      err = _e2;
    },
    f: function f() {
      try {
        if (!normalCompletion && it["return"] != null) it["return"]();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

var HorizontalScrollMobile = function HorizontalScrollMobile(_ref) {
  var _ref$items = _ref.items,
      items = _ref$items === void 0 ? [] : _ref$items,
      numOfVisibleItems = _ref.numOfVisibleItems,
      renderItem = _ref.renderItem,
      className = _ref.className,
      itemClassName = _ref.itemClassName,
      hasFixedSizeItems = _ref.hasFixedSizeItems,
      onItemListScroll = _ref.onItemListScroll,
      onInit = _ref.onInit,
      rest = _objectWithoutProperties(_ref, _excluded);
  /* istanbul ignore next */


  if (!renderItem) {
    renderItem = function renderItem() {
      return null;
    };
  }

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var baseRef = useRef();
  var itemListRef = useRef();
  var visibleItemIdsRef = useRef([]);

  var handleResize = function handleResize() {
    if (hasFixedSizeItems) {
      var baseElm = baseRef.current;
      var item = baseElm.querySelector('li:first-child');
      var itemWidth = item.getBoundingClientRect().width;
      var baseElmWidth = baseElm.getBoundingClientRect().width;
      var peekOffset = _styles.peekWidth + _styles.mobileItemMargin / 2;
      var pageWidth = (itemWidth + _styles.mobileItemMargin) * numOfVisibleItems + peekOffset;
      /* istanbul ignore next */

      if (pageWidth < baseElmWidth) {
        baseElm.style.width = "".concat(pageWidth, "px");
      }
    }
  };
  /* istanbul ignore next */


  useEffect(function () {
    var resizeObserver;

    if (window.ResizeObserver) {
      var item = baseRef.current.querySelector('li:first-child');

      if (hasFixedSizeItems) {
        resizeObserver = new ResizeObserver(function () {
          handleResize();
        });
        resizeObserver.observe(item);
      }
    }

    return function () {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);
  /* istanbul ignore next */

  useEffect(function () {
    var itemObserver;
    handleResize();
    visibleItemIdsRef.current = [];

    if (window.IntersectionObserver) {
      var itemList = itemListRef.current;
      itemObserver = new IntersectionObserver(function (entries) {
        if (!visibleItemIdsRef.current.length) {
          var _iterator = _createForOfIteratorHelper(entries),
              _step;

          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var entry = _step.value;

              if (entry.isIntersecting) {
                var id = entry.target.getAttribute('data-item-id');
                visibleItemIdsRef.current.push(id);
              }
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }

          if (onInit) {
            onInit(visibleItemIdsRef.current);
          }
        } else {
          var _iterator2 = _createForOfIteratorHelper(entries),
              _step2;

          try {
            for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
              var _entry = _step2.value;

              if (_entry.isIntersecting) {
                var _id = _entry.target.getAttribute('data-item-id');

                if (onItemListScroll) {
                  onItemListScroll(_id);
                }
              }
            }
          } catch (err) {
            _iterator2.e(err);
          } finally {
            _iterator2.f();
          }
        }
      }, {
        root: baseRef.current,
        threshold: 1
      });
      var itemElms = itemList.querySelectorAll('li');
      itemElms.forEach(function (item) {
        return itemObserver.observe(item);
      });
    }

    return function () {
      if (itemObserver) {
        itemObserver.disconnect();
      }
    };
  }, [numOfVisibleItems, hasFixedSizeItems]);
  return jsx("div", _extends({
    css: _styles.base,
    className: className,
    ref: baseRef,
    "data-ppui-info": "horizontal-scroll_2.3.13"
  }, rest), jsx(ItemList, {
    items: items,
    hasFixedSizeItems: hasFixedSizeItems,
    isMobileBreakpoint: true,
    numOfVisibleItems: numOfVisibleItems,
    renderItem: renderItem,
    itemClassName: itemClassName,
    ref: itemListRef,
    "data-ppui": "true"
  }));
};

var _excluded$1 = ["items", "hasFixedSizeItems", "isMobileBreakpoint", "itemClassName", "numOfVisibleItems", "renderItem"],
    _excluded2 = ["currentPage", "prevPageLabel", "nextPageLabel", "onPageChange"],
    _excluded3 = ["onItemListScroll", "onInit"];
var ItemList = forwardRef(function (props, ref) {
  var items = props.items,
      hasFixedSizeItems = props.hasFixedSizeItems,
      isMobileBreakpoint = props.isMobileBreakpoint,
      itemClassName = props.itemClassName,
      numOfVisibleItems = props.numOfVisibleItems,
      renderItem = props.renderItem,
      rest = _objectWithoutProperties(props, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  /* istanbul ignore next */


  var handleFocus = function handleFocus(evt) {
    evt.target.scrollIntoView({
      block: 'nearest'
    });
  };

  var itemElements = items.map(function (item, index) {
    var _styles$getFlexBasisI;

    var flexBasisStyles = (_styles$getFlexBasisI = _styles.getFlexBasisItems) === null || _styles$getFlexBasisI === void 0 ? void 0 : _styles$getFlexBasisI.call(_styles, props);
    return jsx("li", {
      "aria-label": "".concat(index + 1, "-").concat(items.length),
      key: item.id,
      "data-item-id": item.id,
      css: [_styles.item, flexBasisStyles],
      className: itemClassName,
      "data-ppui": "true"
    }, renderItem(item, {
      tabIndex: '0',
      role: 'button',
      onFocus: handleFocus
    }));
  });
  return jsx("ul", _extends({
    css: _styles.itemList,
    ref: ref
  }, rest, {
    "data-ppui": "true"
  }), itemElements);
});

var HorizontalScroll = function HorizontalScroll(props) {
  /* istanbul ignore next */
  if (!props.items) {
    console.log('Required prop `items` is missing');
  }
  /* istanbul ignore next */


  if (!props.renderItem) {
    console.log('Required prop `renderItem` is missing');
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showMobileHScroll = _useState2[0],
      setShowMobileHScroll = _useState2[1];

  var _useEmotionTheme = useTheme(),
      sysSizeMediaMd = _useEmotionTheme.sysSizeMediaMd;

  var mdBreakpoint = Number(stripUnit(remToPx(sysSizeMediaMd)));
  /* istanbul ignore next */

  useEffect(function () {
    var mql = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
    var hasHover = mql && mql.matches;
    setShowMobileHScroll(window.innerWidth < mdBreakpoint || window.matchMedia && !hasHover);
  }, []);

  if (showMobileHScroll) {
    var currentPage = props.currentPage,
        prevPageLabel = props.prevPageLabel,
        nextPageLabel = props.nextPageLabel,
        onPageChange = props.onPageChange,
        _rest = _objectWithoutProperties(props, _excluded2);

    return jsx(HorizontalScrollMobile, _extends({}, _rest, {
      "data-ppui": "true"
    }));
  }

  var onItemListScroll = props.onItemListScroll,
      onInit = props.onInit,
      rest = _objectWithoutProperties(props, _excluded3);

  return jsx(HorizontalScrollDesktop, _extends({}, rest, {
    autoAdvance: props.autoAdvance,
    advanceInterval: props.advanceInterval,
    "data-ppui": "true"
  }));
};

var _excluded$2 = ["items", "numOfVisibleItems", "renderItem", "className", "itemClassName", "prevButtonClassName", "nextButtonClassName", "prevPageLabel", "nextPageLabel", "currentPage", "hasFixedSizeItems", "autoAdvance", "advanceInterval", "onPageChange", "style"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var Dots = function Dots(_ref) {
  var numOfPages = _ref.numOfPages,
      currentPage = _ref.currentPage;
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var dotElements = [];

  for (var i = 1; i <= numOfPages; i++) {
    var dotStyles = [_styles.dot, currentPage === i && _styles.dotSelected];
    dotElements.push(jsx("div", {
      key: i,
      css: dotStyles,
      "data-ppui": "true"
    }));
  }

  return jsx("div", {
    css: _styles.dots,
    "aria-hidden": true,
    "data-ppui": "true"
  }, dotElements);
};
/*
 * Turns out that using setInterval() in react with hooks is awkward.
 * This method I lifted from Dan Abramov makes it easy to use in a
 * react hook-ish way.
 * Pass in a callback and a delay in ms or null to turn it off
 * --love, BCC
 */

/* istanbul ignore next */


function useInterval(callback, delay) {
  var savedCallback = useRef(); // Remember the latest function.

  useLayoutEffect(function () {
    savedCallback.current = callback;
  }, [callback]); // Set up the interval.

  useLayoutEffect(function () {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      var id = setInterval(tick, delay);
      return function () {
        clearInterval(id);
      };
    }

    return undefined;
  }, [delay]);
}
/* (from the Jira spec)
  Auto-advance activates on a new page load.
  It will advance every 4 seconds by default, but the caller can specify a different timing.
  It will stop if the user scrolls the horizontal scroll out of view, interacts with cards, or clicks a button.
  It will also stop after 1 full cycle.
 */


var HorizontalScrollDesktop = function HorizontalScrollDesktop(_ref2) {
  var _styles$getContainerW, _styles$getContainerW2;

  var _ref2$items = _ref2.items,
      items = _ref2$items === void 0 ? [] : _ref2$items,
      _ref2$numOfVisibleIte = _ref2.numOfVisibleItems,
      numOfVisibleItems = _ref2$numOfVisibleIte === void 0 ? 1 : _ref2$numOfVisibleIte,
      renderItem = _ref2.renderItem,
      className = _ref2.className,
      itemClassName = _ref2.itemClassName,
      prevButtonClassName = _ref2.prevButtonClassName,
      nextButtonClassName = _ref2.nextButtonClassName,
      _ref2$prevPageLabel = _ref2.prevPageLabel,
      prevPageLabel = _ref2$prevPageLabel === void 0 ? 'Scroll left' : _ref2$prevPageLabel,
      _ref2$nextPageLabel = _ref2.nextPageLabel,
      nextPageLabel = _ref2$nextPageLabel === void 0 ? 'Scroll right' : _ref2$nextPageLabel,
      _ref2$currentPage = _ref2.currentPage,
      currentPage = _ref2$currentPage === void 0 ? 1 : _ref2$currentPage,
      hasFixedSizeItems = _ref2.hasFixedSizeItems,
      _ref2$autoAdvance = _ref2.autoAdvance,
      autoAdvance = _ref2$autoAdvance === void 0 ? false : _ref2$autoAdvance,
      _ref2$advanceInterval = _ref2.advanceInterval,
      advanceInterval = _ref2$advanceInterval === void 0 ? 4000 : _ref2$advanceInterval,
      onPageChange = _ref2.onPageChange,
      style = _ref2.style,
      rest = _objectWithoutProperties(_ref2, _excluded$2);
  /* istanbul ignore next */


  if (!renderItem) {
    renderItem = function renderItem() {
      return null;
    };
  }

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var baseRef = useRef(null);
  var scrollPanel = useRef(null);
  var pageContainerRef = useRef();
  var itemListRef = useRef();

  var _useState = useState(Math.ceil(items.length / numOfVisibleItems)),
      _useState2 = _slicedToArray(_useState, 2),
      numOfPages = _useState2[0],
      setNumOfPages = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isAutoAdvance = _useState4[0],
      setIsAutoAdvance = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isRTL = _useState6[0],
      setRTL = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      pageWidth = _useState8[0],
      setPageWidth = _useState8[1];

  var viewObserver = useRef(null);
  var hasBeenVisibleRef = useRef(false);
  var autoAdvanceComplete = useRef(false);
  /* istanbul ignore next */

  var handleResize = function handleResize() {
    var pageContainer = pageContainerRef.current;

    if (pageContainer) {
      if (hasFixedSizeItems) {
        var item = pageContainer.querySelector('li:first-child');
        var itemWidth = item.getBoundingClientRect().width;
        var newNumOfPages = Math.ceil(items.length / numOfVisibleItems);
        setPageWidth((itemWidth + _styles.itemMargin) * numOfVisibleItems);
        setNumOfPages(newNumOfPages);

        if (currentPage > newNumOfPages) {
          onPageChange(newNumOfPages);
        }
      } else {
        var itemList = itemListRef.current; // scrollwidth always returns a whole value, getBoundingClientRect().width sometimes returns value comparing
        // pageContainerWidth with scrollWidth when the width causes an overflow resulting in adding and extra page.
        // e.g. scrollWidth = 1201, vs pageContainerWidth = 1200.092

        var itemListScrollWidth = itemList === null || itemList === void 0 ? void 0 : itemList.scrollWidth;
        var pageContainerWidth = Math.ceil(pageContainer.getBoundingClientRect().width);

        if (itemListScrollWidth && pageContainerWidth) {
          var _newNumOfPages = Math.ceil(itemListScrollWidth / pageContainerWidth);

          setNumOfPages(_newNumOfPages);

          if (currentPage > _newNumOfPages) {
            onPageChange(_newNumOfPages);
          }
        }

        setPageWidth(pageContainerWidth);
      }
    }
  };
  /* istanbul ignore next */

  /* istanbul ignore next */


  var stopAutoAdvance = function stopAutoAdvance() {
    setIsAutoAdvance(false);
    autoAdvanceComplete.current = true; // eslint-disable-next-line no-unused-expressions

    viewObserver && viewObserver.current && viewObserver.current.disconnect();
  };
  /* istanbul ignore next */

  /* istanbul ignore next */


  var handleIntersection = function handleIntersection(entries) {
    var _entries = _slicedToArray(entries, 1),
        entry = _entries[0]; // No intersection between hscroll & viewport?
    // ...then we've scrolled off screen - stop auto advance


    if (!entry.isIntersecting) {
      if (hasBeenVisibleRef.current) {
        stopAutoAdvance();
      }
    } else if (autoAdvance && !hasBeenVisibleRef.current) {
      setIsAutoAdvance(!autoAdvanceComplete.current);
      hasBeenVisibleRef.current = true;
    }
  };
  /* istanbul ignore next */


  useEffect(function () {
    var computedStyle = window.getComputedStyle(baseRef.current);
    setRTL(computedStyle && computedStyle.direction === 'rtl');
    var resizeObserver;

    if (window.ResizeObserver && baseRef.current) {
      var item = baseRef.current.querySelector('li:first-child');
      resizeObserver = new ResizeObserver(function () {
        handleResize();
      });
      resizeObserver.observe(hasFixedSizeItems ? item : pageContainerRef.current);
    }

    return function () {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);
  useEffect(function () {
    /* istanbul ignore next */
    if (items.length && numOfVisibleItems) {
      window.requestAnimationFrame(handleResize);
    }
  }, [numOfVisibleItems, hasFixedSizeItems, items]);
  useEffect(function () {
    /* istanbul ignore next */
    setIsAutoAdvance(autoAdvance && hasBeenVisibleRef.current && !autoAdvanceComplete.current);
  }, [autoAdvance, hasBeenVisibleRef.current]);
  /* istanbul ignore next */

  useInterval(function handleAutoAdvance() {
    var nextPage = currentPage + 1;

    if (currentPage >= numOfPages) {
      // Have we reached the last page?
      stopAutoAdvance(); // We're done with auto advance

      var panel = scrollPanel.current;
      panel.style.transition = '0s linear';
      panel.style.left = '0px';
      setTimeout(function () {
        panel.style.transition = '';
      }, 100);
      nextPage = 1; // Go back to first page
    }

    onPageChange(nextPage);
  }, isAutoAdvance ? advanceInterval : null);
  useLayoutEffect(function () {
    /* istanbul ignore next */
    if (window && window.IntersectionObserver) {
      var hscroll = baseRef.current;

      if (autoAdvance && !autoAdvanceComplete.current) {
        viewObserver.current = new window.IntersectionObserver(handleIntersection);
        viewObserver.current.observe(hscroll);
      }
    }
  }, [autoAdvance, autoAdvanceComplete.current]);
  /* istanbul ignore next */

  useEffect(function () {
    if (!window.IntersectionObserver) {
      return undefined;
    }

    var hscroll = baseRef.current;

    var stopObserving = function stopObserving() {
      if (viewObserver && viewObserver.current) {
        viewObserver.current.unobserve(hscroll);
      }
    };

    if (autoAdvance) {
      viewObserver.current.observe(hscroll);
    } else {
      stopObserving();
    }

    return stopObserving;
  }, [baseRef, autoAdvance]);
  /* istanbul ignore next */

  var renderPreviousButton = currentPage > 1 ? jsx("div", {
    css: _styles.buttonPrevious,
    "data-ppui": "true"
  }, jsx(IconButton, {
    buttonType: "secondary",
    inverse: true,
    svgIconComponent: isRTL ? ArrowRightIcon : ArrowLeftIcon,
    tabIndex: -1,
    "aria-hidden": true,
    onClick: function handlePrevClick() {
      onPageChange(currentPage - 1);
      setIsAutoAdvance(false);
    },
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    className: prevButtonClassName,
    "data-ppui": "true"
  }, prevPageLabel)) : null;
  /* istanbul ignore next */

  var renderNextButton = currentPage < numOfPages ? jsx("div", {
    css: _styles.buttonNext,
    "data-ppui": "true"
  }, jsx(IconButton, {
    buttonType: "secondary",
    inverse: true,
    svgIconComponent: isRTL ? ArrowLeftIcon : ArrowRightIcon,
    tabIndex: -1,
    "aria-hidden": true,
    onClick: function handleNextClick() {
      onPageChange(currentPage + 1);
      setIsAutoAdvance(false);
    },
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    className: nextButtonClassName,
    "data-ppui": "true"
  }, nextPageLabel)) : null;
  /* istanbul ignore next */

  var renderDots = numOfPages > 1 ? jsx(Dots, {
    numOfPages: numOfPages,
    currentPage: currentPage,
    "data-ppui": "true"
  }) : null;
  var pageOffset = -pageWidth * (currentPage - 1);
  /* istanbul ignore next */

  var pageStyle = isRTL ? {
    right: pageOffset
  } : {
    left: pageOffset
  };
  return jsx("div", _extends({
    css: [_styles.base, (_styles$getContainerW = _styles.getContainerWidth) === null || _styles$getContainerW === void 0 ? void 0 : _styles$getContainerW.call(_styles, pageWidth, hasFixedSizeItems)],
    className: className,
    ref: baseRef,
    onFocus: autoAdvance ? function handleFocus() {
      setIsAutoAdvance(false);
    } : null,
    style: _objectSpread({}, style),
    "data-ppui-info": "horizontal-scroll_2.3.13"
  }, rest), jsx("div", {
    css: [_styles.pageContainer, (_styles$getContainerW2 = _styles.getContainerWidth) === null || _styles$getContainerW2 === void 0 ? void 0 : _styles$getContainerW2.call(_styles, pageWidth, hasFixedSizeItems)],
    ref: pageContainerRef,
    "data-ppui": "true"
  }, jsx("div", {
    css: _styles.page,
    style: pageStyle,
    ref: scrollPanel,
    "data-ppui": "true"
  }, jsx(ItemList, {
    items: items,
    hasFixedSizeItems: hasFixedSizeItems,
    isMobileBreakpoint: false,
    numOfVisibleItems: numOfVisibleItems,
    renderItem: renderItem,
    itemClassName: itemClassName,
    ref: itemListRef,
    css: _styles.itemListDesktop,
    "data-ppui": "true"
  }))), renderPreviousButton, renderNextButton, renderDots);
};

export { HorizontalScroll, HorizontalScrollDesktop, HorizontalScrollMobile, ItemList, styles as horizontalScrollStyles };